import { ReactElement, createContext, useCallback, useContext, useEffect, useState } from 'react';

import TawkMessengerReact from '@tawk.to/tawk-messenger-react';
import { CognitoUser, CognitoUserAttribute, CognitoUserPool, CognitoUserSession } from 'amazon-cognito-identity-js';
import { AxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import { IUserRole, MfaMethod } from 'types';
import { UserRole } from '../../../types';
import { Method, queryData } from '../../../utilities/fetch';
import SpinnerModal from '../../molecules/SpinnerModal';
import { MfaAuthOnly } from '../../organisms/mfa/mfaAuth/mfaAuthOnly';
import MfaSetupSMS from '../../organisms/mfa/mfaSetup/mfaSetupSms';
import MfaSetupTotp from '../../organisms/mfa/mfaSetup/mfaSetupTotp';
import CookieStorage from './cookie-storage';
import ForgotPassword from './forgot-password';
import ForgotPasswordSent from './forgot-password-sent';
import IdleLoggedOut from './idle-logout';
import Login from './login';
import './login.css';
import MfaSetup from './mfa-setup';
import NewPassword from './new-password';
import onForgotPassword from './onForgotPassword';
import onLoginClick from './onLoginClick';
import onNewPasswordSubmit from './onNewPasswordSubmit';
import PageWrapper from './page-wrapper';
import { refreshSession } from './refreshSession';
import SingleSignOnCallback from './sso/login';
import SSOLogout from './sso/logout';

interface AuthenticationProps {
  assetsUrl: string;
  userPoolId: string;
  appClient: string;
  mfaEnabled: boolean;
  code?: string;
  uniCode: string;
  apiUrl: string;
  children: ReactElement;
  samlEnabled?: boolean;
  isStudentApp?: boolean;
  cognitoDomain?: string;
  cognitoProviderName?: string;
  redirectUrl?: string;
}

export interface ISessionContext {
  getIdToken?: Function;
  getAccessToken?: Function;
  onLogout?: Function;
  appRefreshSession?: Function;
  isSessionValid?: boolean;
  groups?: Array<IUserRole>;
  username?: string;
  uni?: string;
  pathway?: string;
  studentNo?: string;
  pathwayAccess?: string;
  terms?: string;
  preferredUsername?: string;
  agency?: string;
  email?: string;
}

export enum AuthStage {
  'LOGIN' = 'LOGIN',
  'FORGOT' = 'FORGOT',
  'FORGOT_SENT' = 'FORGOT_SENT',
  'NEW_PASSWORD' = 'NEW_PASSWORD',
  'MFA_SETUP' = 'MFA_SETUP',
  'MFA_SETUP_TOTP' = 'MFA_SETUP_TOTP',
  'MFA_SETUP_TOTP_CODE' = 'MFA_SETUP_TOTP_CODE',
  'MFA_SETUP_SMS' = 'MFA_SETUP_SMS',
  'MFA_SETUP_SMS_CODE' = 'MFA_SETUP_SMS_CODE',
  'TOTP_CODE' = 'TOTP_CODE',
  'SMS_CODE' = 'SMS_CODE',
  'CHANGE_PASSWORD' = 'CHANGE_PASSWORD',
  'IDLE_LOGOUT' = 'IDLE_LOGOUT',
  'SSO_CALLBACK' = 'SSO_CALLBACK',
  'SSO_LOGOUT' = 'SSO_LOGOUT',
  'NONE' = 'NONE'
}
const env = process?.env?.NODE_ENV;

export let cookieStorage; //: CookieStorage;

export const SessionContext = createContext({});

function Authentication(props: AuthenticationProps): JSX.Element {
  const {
    uniCode,
    mfaEnabled,
    code,
    apiUrl,
    samlEnabled,
    assetsUrl,
    isStudentApp,
    cognitoDomain,
    cognitoProviderName,
    redirectUrl,
    userPoolId,
    appClient
  } = props;
  const isDev =
    env === 'development' || typeof window !== 'undefined' ? window?.location?.hostname === 'localhost' : false;

  const domain = isDev ? 'localhost' : window.location.hostname;
  cookieStorage = new CookieStorage({
    domain,
    secure: true,
    sameSite: 'strict',
    path: '/'
  });

  // Why do we default this to a cookie value I can't think of a scenario where this is beneficial
  const [username, setUsername] = useState(cookieStorage?.getItem('username') ?? '');
  //const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Array<string>>([]);
  const [authStage, setAuthStage] = useState<AuthStage>(AuthStage.LOGIN);
  const [isLoading, setIsLoading] = useState(true);
  const [hashRedirect, setHashRedirect] = useState(false);
  const [user, setUser] = useState<CognitoUser>();
  const [session, setSession] = useState<CognitoUserSession>();
  const [secretCode, setSecretCode] = useState('');
  const [ssoCode, setSSOCode] = useState(undefined);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [resetPasswordCode, setResetPasswordCode] = useState('');
  const [showTawk, setShowTawk] = useState(false);

  async function appRefreshSession(): Promise<CognitoUserSession | null> {
    try {
      const result = await refreshSession({
        userPoolId,
        appClient,
        username
      });
      if (!result || !result.session?.isValid?.()) {
        toggleTawkWidget({ hide: false });
        setIsLoading(false);
        setUser(null);
        setSession(null);
        cookieStorage.removeItem('username');
        cookieStorage.removeItem('appClient');
        cookieStorage.removeItem('apiUrl');
        cookieStorage.removeItem('uni');
        return null;
      }

      const { session, user: cognitoUser } = result;

      isStudentApp && toggleTawkWidget({ hide: true });
      setSession(session);
      setUser(cognitoUser);
      setIsLoading(false);
      cookieStorage.setItem('username', username);
      cookieStorage.setItem('appClient', appClient); // for NextJS app
      cookieStorage.setItem('apiUrl', apiUrl); // for NextJS app
      cookieStorage.setItem('uni', uniCode); // for NextJS app
      return session;
    } catch (e) {
      setIsLoading(false);
      setUser(null);
      setSession(null);
      if (e.message === 'Refresh Token has been revoked') {
        if (isStudentApp) {
          toast.error(
            `Attention: Multiple CAS Shield sessions detected. To ensure accurate system usage, you have been automatically logged out. Please return to the other session you have open to continue using CAS Shield.`,
            {
              toastId: 'reauthenticateUser'
            }
          );
        } else {
          await onLogout(true);
        }
      } else {
        setErrors([e.message]);
      }
      return null;
    }
  }

  useEffect(() => {
    // Called initially on page load / refresh
    setIsInitialLoad(true);
    const init = async () => {
      if (window.location.pathname.includes('changePassword')) {
        setIsInitialLoad(false);
        return;
      }
      if (window.location.pathname.includes('/auth/logout')) {
        // for Insights app
        await onLogout();
        setIsInitialLoad(false);
        setIsLoading(false);
        return;
      }

      if (samlEnabled) {
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        setSSOCode(code);

        if (window.location.pathname.includes('/auth/sso/callback')) {
          setAuthStage(AuthStage.SSO_CALLBACK);
          setIsInitialLoad(false);
          return;
        }
      }

      if (window.location.pathname.includes('resetPassword')) {
        const urlParams = new URLSearchParams(window.location.search);
        setUsername(urlParams.get('username'));
        setResetPasswordCode(urlParams.get('code'));
        setIsInitialLoad(false);
        setIsLoading(false);
        setAuthStage(AuthStage.CHANGE_PASSWORD);
        return;
      }

      if (!session?.isValid()) {
        let refreshSession: CognitoUserSession | null = null;

        try {
          refreshSession = await appRefreshSession();
        } catch (err) {
          console.log('session refresh failed, user logged out');
        }
        setIsInitialLoad(false);
        // if samlUser and refresh session didn't work, then attempt to login via SSO
        if (
          samlEnabled &&
          refreshSession === null &&
          !window.location.pathname.includes('/auth/sso/callback') &&
          authStage !== AuthStage.SSO_LOGOUT
        ) {
          // console.log('session not valid - attempting to SSO login');
          await onSingleSignOnLogin();
        }
      } else if (isStudentApp) {
        toggleTawkWidget({ hide: true });
      }
    };
    init();
  }, []);

  useEffect(() => {
    const w = window as any;
    w.Enroly = {};
    if (
      (w.location.hostname.endsWith('.net') && !w.location.hostname.includes('srp-auth-v9')) ||
      w.location.hostname.includes('localhost')
    ) {
      w.Enroly.isDevEnv = true;
    }
    if (w.Cypress) {
      w.Enroly.isTestEnv = true;
    }
  }, []);

  // called by app before making api requests
  async function getIdToken(isTesting?: boolean): Promise<string> {
    if (isTesting) {
      return 'fakeToken';
    }

    try {
      if (session?.isValid()) {
        return session.getIdToken().getJwtToken();
      }

      const refreshSession = await appRefreshSession();
      if (!refreshSession) {
        return '';
      }

      const idToken = refreshSession.getIdToken().getJwtToken();
      return idToken;
    } catch (e) {
      console.log('error while getId Token', e);
      return '';
    }
  }
  // only user for a recorded interview
  async function getAccessToken(isTesting?: boolean): Promise<string> {
    if (isTesting) {
      return 'fakeToken';
    }
    return session?.getIdToken?.()?.getJwtToken?.() ?? '';
  }

  useEffect(() => {
    // for unsubscribe, set visa status etc. on student app
    if (code) {
      const parsedHash = atob(code);
      if (typeof parsedHash === 'string') {
        const { email, password } = JSON.parse(parsedHash);

        setUsername(email);
        setPassword(password);
      }
    }
  }, []);

  const getUser = useCallback(() => {
    if (cookieStorage && userPoolId && appClient && username) {
      const userPool = new CognitoUserPool({
        UserPoolId: userPoolId,
        ClientId: appClient,
        Storage: cookieStorage
      });
      const newUser = new CognitoUser({
        Username: username,
        Pool: userPool!,
        Storage: cookieStorage
      });
      setUser(newUser);
      return newUser;
    }
    return undefined;
  }, [session?.isValid(), session, username, password]);

  useEffect(() => {
    if (username) {
      setHashRedirect(true);
    }
  }, [username]);

  useEffect(() => {
    // for unsubscribe, set visa status etc. on student app
    const handleLoginIn = async () => {
      if (code && username && password && hashRedirect) {
        return await handleLoginClick();
      }
    };
    handleLoginIn();
  }, [hashRedirect]);

  const onSingleSignOnLogin = () => {
    if (!samlEnabled || window.location.pathname.includes('/auth/sso/callback') || session?.isValid()) {
      return;
    }
    const params = new URLSearchParams();
    params.append('identity_provider', cognitoProviderName);
    params.append('redirect_uri', redirectUrl);
    params.append('response_type', 'code');
    params.append('client_id', appClient);
    params.append('scope', 'aws.cognito.signin.user.admin openid');
    const url = `${cognitoDomain}/oauth2/authorize?${params.toString()}`;

    window.location.href = url;
  };

  function handleUserNameChange(e: any) {
    setUsername(e.currentTarget.value);
    setErrors([]);
  }

  function handlePasswordChange(e: any) {
    setPassword(e.currentTarget.value);
    setErrors([]);
  }

  function handleForgotPassword(evt: any) {
    evt?.preventDefault && evt.preventDefault();
    try {
      const cognitoUser = getUser();

      onForgotPassword({
        user: cognitoUser,
        setAuthStage
      });
    } catch (err) {
      setErrors([err.message]);
    }
  }

  async function handleLoginClick() {
    try {
      const cognitoUser = getUser();

      const session = await onLoginClick({
        mfaEnabled,
        username,
        password,
        user: cognitoUser,
        setAuthStage,
        setPassword,
        setIsLoading,
        appClient: props.appClient,
        apiUrl,
        uniCode
      });
      isStudentApp && toggleTawkWidget({ hide: true });
      setIsLoading(false);
      setSession(session);

      if (code) {
        setAuthStage(AuthStage.NEW_PASSWORD);
      }
    } catch (e: any) {
      const error = e as AxiosError;
      if (error?.response?.data === 'Refresh Token has been revoked' && isStudentApp) {
        setErrors([
          `Attention: Multiple CAS Shield sessions detected. To ensure accurate system usage, you have been automatically logged out. Please return to the other session you have open to continue using CAS Shield.`
        ]);
      } else {
        setErrors([e.message]);
      }
    }
  }

  async function handleNewPasswordSubmit() {
    let cognitoUser = user;
    if (window.location.pathname.includes('resetPassword')) {
      cognitoUser = getUser();
    }
    try {
      const session = await onNewPasswordSubmit({
        mfaEnabled: props.mfaEnabled,
        username,
        password,
        user: cognitoUser,
        code: resetPasswordCode,
        setPassword,
        setAuthStage,
        setIsLoading,
        appClient,
        apiUrl
      });

      // default change password from initial login
      if (session instanceof CognitoUserSession) {
        setSession(session);
        setAuthStage(AuthStage.NONE);
        window.history.replaceState(null, '', '/');
      }
      // reset password
      if (session === 'SUCCESS' && resetPasswordCode) {
        const session = await onLoginClick({
          mfaEnabled,
          username,
          password,
          user: cognitoUser,
          setAuthStage,
          setPassword,
          setIsLoading,
          appClient,
          apiUrl,
          uniCode
        });
        // only set session if successful / else redirecting to MFA screen / failure
        if (session) {
          setSession(session);
          setAuthStage(AuthStage.NONE);
          window.history.replaceState(null, '', '/');
        }
      }
      setIsLoading(false);
    } catch (err) {
      setErrors([err.message]);
    }
  }

  function handleSkip() {
    sessionStorage.setItem('mfa.setupSkipped', 'true');
    setAuthStage(AuthStage.NONE);
  }

  function handleSelectSMS() {
    setAuthStage(AuthStage.MFA_SETUP_SMS);
  }

  function handleSelectTOTP() {
    const associateSecretCode = (secretCode: string) => {
      setSecretCode(secretCode);
      setAuthStage(AuthStage.MFA_SETUP_TOTP);
    };

    const onFailure = (e: any) => {
      console.error(e);
    };
    if (secretCode === '') {
      user?.associateSoftwareToken({ associateSecretCode, onFailure });
    } else {
      setAuthStage(AuthStage.MFA_SETUP_TOTP);
    }
  }

  function handleChooseDifferentMethod() {
    setAuthStage(AuthStage.MFA_SETUP);
  }

  function handleTOTPMfaSubmit() {
    setAuthStage(AuthStage.MFA_SETUP_TOTP_CODE);
  }

  function handleSMSMFASubmit(phone: string) {
    const onSuccess = () => {
      setAuthStage(AuthStage.MFA_SETUP_SMS_CODE);
    };

    const onFailure = (err: Error) => {
      setErrors([err.message]);
    };

    // SMS MFA fails when a user has an apostrophe in their username. The actual MFA API request
    // is successful and the session data is updated but the Device API request made after fails
    if (username.includes("'")) {
      const session = user.getSignInUserSession();
      setSession(session);
    }

    user?.updateAttributes(
      [
        new CognitoUserAttribute({
          Name: 'phone_number',
          Value: phone
        })
      ],
      (err?: Error) => {
        if (err) {
          setErrors([err.message]);
        } else {
          user.getAttributeVerificationCode('phone_number', { onSuccess, onFailure });
        }
      }
    );
  }

  function handleSMSMFACodeSubmit(code: string) {
    const onSuccess = async () => {
      const onMFAResult = async (err: Error | undefined) => {
        if (err) {
          setErrors([err.message]);
        } else {
          setAuthStage(AuthStage.NONE);
          await handleMfaStatusUpdate(MfaMethod.SMS);
        }
      };

      user?.setUserMfaPreference(
        {
          Enabled: true,
          PreferredMfa: true
        },
        null,
        onMFAResult
      );
    };

    const onFailure = (err: Error) => {
      setErrors([err.message]);
    };

    user?.verifyAttribute('phone_number', code, { onSuccess, onFailure });
  }

  function handleMFASetupCodeSubmit(code: string) {
    const onSuccess = async () => {
      const onMFAResult = async (err: Error | undefined) => {
        if (err) {
          setErrors([err.message]);
        } else {
          await handleMfaStatusUpdate(MfaMethod.TOTP);
          setAuthStage(AuthStage.NONE);
        }
      };

      user?.setUserMfaPreference(
        null,
        {
          Enabled: true,
          PreferredMfa: true
        },
        onMFAResult
      );
    };

    const onFailure = (err: Error) => {
      setErrors([err.message]);
    };
    user?.verifySoftwareToken(code, 'enroly', { onSuccess, onFailure });
  }

  function onMFASuccess(session: CognitoUserSession) {
    setSession(session);
    setAuthStage(AuthStage.NONE);
  }

  function onMFAFailure(err) {
    // user.sendMFACode() fails when a user has an apostrophe in their username. The SendMFA API request
    // is successful and the session data is updated but the Device API request made after fails
    if (err?.code === 'InvalidParameterException' && username.includes("'")) {
      const session = user.getSignInUserSession();
      setSession(session);
      setAuthStage(AuthStage.NONE);
    } else {
      setErrors([err.message]);
    }
  }

  function handleTOTPCodeSubmit(code: string) {
    user?.sendMFACode(code, { onSuccess: onMFASuccess, onFailure: onMFAFailure }, 'SOFTWARE_TOKEN_MFA');
  }

  function handleSMSCodeSubmit(code: string) {
    user?.sendMFACode(code, { onSuccess: onMFASuccess, onFailure: onMFAFailure });
  }

  function handleMFAHelp() {
    window?.open('https://www.enroly.com/faqs/mfa', '_blank');
  }

  function handleResendSMSCode() {
    user?.getAttributeVerificationCode('phone_number', { onSuccess: () => null, onFailure: () => null });
  }
  const onLogout = useCallback(
    async function logout(isIdleLogout?: boolean) {
      let cognitoUser = user;
      // when logging out from Insights the user is not populated
      if (!cognitoUser) {
        cognitoUser = getUser();
      }

      cognitoUser?.signOut(() => {
        setIsLoading(false);
      });
      // reset auth vars
      setSession(null);
      setUsername('');
      cookieStorage.removeItem('username');
      cookieStorage.removeItem('appClient');
      cookieStorage.removeItem('apiUrl');
      cookieStorage.removeItem('uni');
      setPassword('');
      setErrors([]);
      toggleTawkWidget({ hide: false });

      if (samlEnabled) {
        setAuthStage(isIdleLogout ? AuthStage.IDLE_LOGOUT : AuthStage.SSO_LOGOUT);
      } else {
        setAuthStage(isIdleLogout ? AuthStage.IDLE_LOGOUT : AuthStage.LOGIN);
        if (!isStudentApp && !window.location.pathname.includes('/auth/logout')) {
          window.localStorage.setItem('redirect', `${window.location.pathname}${window.location.search}`);
        }
        window.history.replaceState(null, '', '/');
      }
      // TBC look to use globalSignOut - signs the user out of all devices
    },
    [user]
  );

  let element;
  switch (authStage) {
    case AuthStage.FORGOT:
      element = (
        <ForgotPassword
          username={username}
          errors={errors}
          onUsernameChange={handleUserNameChange}
          onForgotPasswordSubmit={handleForgotPassword}
          onLoginClick={() => setAuthStage(AuthStage.LOGIN)}
          isStudentApp={isStudentApp}
        />
      );
      break;
    case AuthStage.FORGOT_SENT:
      element = (
        <ForgotPasswordSent
          errors={errors}
          username={username}
          onForgotPasswordSubmit={handleForgotPassword}
          onLoginClick={() => setAuthStage(AuthStage.LOGIN)}
        />
      );
      break;
    case AuthStage.CHANGE_PASSWORD:
    case AuthStage.NEW_PASSWORD:
      element = (
        <NewPassword
          password={password}
          errors={errors}
          onPasswordChange={handlePasswordChange}
          onPasswordSubmit={handleNewPasswordSubmit}
        />
      );
      break;
    case AuthStage.MFA_SETUP:
      element = <MfaSetup onSelectSMS={handleSelectSMS} onSelectTOTP={handleSelectTOTP} onSkip={handleSkip} />;
      break;
    case AuthStage.MFA_SETUP_TOTP:
      element = (
        <MfaSetupTotp
          totpAppLabel={`CAS Shield:${uniCode}`}
          secretCode={secretCode}
          onSignOut={() => null}
          onSubmit={handleTOTPMfaSubmit}
          onChooseDifferent={handleChooseDifferentMethod}
        />
      );
      break;
    case AuthStage.MFA_SETUP_TOTP_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.TOTP}
          serverErrors={errors}
          onSubmit={handleMFASetupCodeSubmit}
          onResendCode={() => null}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
        />
      );
      break;
    case AuthStage.MFA_SETUP_SMS:
      element = (
        <MfaSetupSMS
          onSignOut={() => null}
          onSubmit={handleSMSMFASubmit}
          onChooseDifferent={handleChooseDifferentMethod}
        />
      );
      break;
    case AuthStage.MFA_SETUP_SMS_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.SMS}
          serverErrors={errors}
          onSubmit={handleSMSMFACodeSubmit}
          onResendCode={handleResendSMSCode}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
        />
      );
      break;
    case AuthStage.TOTP_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.TOTP}
          serverErrors={errors}
          onSubmit={handleTOTPCodeSubmit}
          onResendCode={() => null}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
        />
      );
      break;
    case AuthStage.SMS_CODE:
      element = (
        <MfaAuthOnly
          uniLogo={''}
          method={MfaMethod.SMS}
          serverErrors={errors}
          onSubmit={handleSMSCodeSubmit}
          onResendCode={handleResendSMSCode}
          onAuthHelp={handleMFAHelp}
          onSignOut={() => null}
          isMobile={false}
        />
      );
      break;
    case AuthStage.IDLE_LOGOUT:
      element = (
        <IdleLoggedOut
          onLoginClick={() => {
            if (samlEnabled) {
              window.location.href = '/';
            } else {
              setAuthStage(AuthStage.LOGIN);
            }
          }}
        />
      );
      break;
    case AuthStage.SSO_CALLBACK:
      element = (
        <SingleSignOnCallback
          code={ssoCode}
          apiUrl={apiUrl}
          appClient={appClient}
          setAuthStage={setAuthStage}
          setUser={setUser}
          setSession={setSession}
          setUsername={setUsername}
          setIsLoading={setIsLoading}
        />
      );
      break;
    case AuthStage.SSO_LOGOUT:
      element = (
        <SSOLogout
          appClient={appClient}
          cognitoDomain={cognitoDomain}
          onLogout={onLogout}
          setAuthStage={setAuthStage}
        />
      );
      break;
    default:
      element = (
        <Login
          isStudentApp={isStudentApp}
          errors={errors}
          username={username}
          password={password}
          samlEnabled={samlEnabled}
          onUsernameChange={handleUserNameChange}
          onPasswordChange={handlePasswordChange}
          onForgotPasswordClick={() => setAuthStage(AuthStage.FORGOT)}
          onLoginClick={handleLoginClick}
          setIsLoading={setIsLoading}
        />
      );
      break;
  }

  const AUTH_STAGES = [
    AuthStage.MFA_SETUP,
    AuthStage.MFA_SETUP_SMS,
    AuthStage.MFA_SETUP_SMS_CODE,
    AuthStage.MFA_SETUP_TOTP,
    AuthStage.MFA_SETUP_TOTP_CODE,
    AuthStage.SMS_CODE,
    AuthStage.TOTP_CODE,
    AuthStage.CHANGE_PASSWORD,
    AuthStage.SSO_CALLBACK,
    AuthStage.SSO_LOGOUT
  ];
  const isAuthPage = AUTH_STAGES.includes(authStage);

  const toggleTawkWidget = ({ hide }: { hide: boolean }) => {
    toggleTawk({ hide });
    setShowTawk(!hide);
  };

  const toggleTawk = ({ hide }: { hide: boolean }) => {
    const w = window as any;
    if (w.Tawk_API) {
      if (hide) {
        w.Tawk_API.hideWidget && w.Tawk_API.hideWidget();
      } else {
        w.Tawk_API.showWidget && w.Tawk_API.showWidget();
      }
    }
  };

  const handleMfaStatusUpdate = async (mfaMethod: MfaMethod) => {
    try {
      const token = session?.getIdToken()?.getJwtToken();
      let decodedToken: any;
      if (token) {
        decodedToken = jwtDecode(token);
      }
      if (decodedToken) {
        const username = decodedToken['cognito:username'];
        const pathway = decodedToken['custom:Pathway'];
        // TODO: Give this correct request and response types
        const resp = await queryData<any, any>({
          uri: `/users/mfa/status`,
          idToken: token,
          domain: apiUrl,
          method: Method.Post,
          body: { clientId: appClient, mfaMethod, username, pathway }
        });
        return resp;
      }
    } catch (err) {
      console.log('err on handleMdaStatusUpdate', err);
    }
  };

  if (session?.isValid() && !isAuthPage) {
    const token = session.getIdToken().getJwtToken();
    const decodedToken: any = jwtDecode(token);
    const nextUrl = window.localStorage.getItem('redirect');
    if (!isStudentApp && nextUrl) {
      window.localStorage.removeItem('redirect');
      // prevent insights loop when app not loading properly
      if (nextUrl.includes('insights')) {
        window.localStorage.setItem('redirectedToInsights', 'true');
      }
      if (window.localStorage.getItem('redirectedFromInsights')) {
        window.localStorage.removeItem('redirectedFromInsights');
      } else {
        window.location.href = `${window.location.origin}${nextUrl}`;
        return <SpinnerModal show={!!isLoading} />;
      }
    }

    return (
      <>
        <SessionContext.Provider
          value={{
            getIdToken,
            getAccessToken,
            onLogout,
            appRefreshSession,
            studentNo: decodedToken['custom:StudentNo'],
            uni: decodedToken['custom:University'] || uniCode,
            pathway: decodedToken['custom:Pathway'],
            pathwayAccess: decodedToken['custom:PathwayAccess'],
            groups: decodedToken['cognito:groups'] ?? (isStudentApp ? '' : UserRole.Unknown),
            terms: decodedToken['custom:Terms'],
            preferredUsername: decodedToken['preferred_username'],
            username: decodedToken['cognito:username'],
            agency: decodedToken['custom:Agency'],
            email: decodedToken['email'],
            isSessionValid: session?.isValid()
          }}
        >
          {props.children}
        </SessionContext.Provider>
      </>
    );
  } else {
    return (
      <>
        {isStudentApp && showTawk && !((window as any).isDevEnv || (window as any).isTestEnv) ? (
          <TawkMessengerReact propertyId="5ff82fa1a9a34e36b96a4dc0" widgetId="1ergnk4i0" />
        ) : null}
        <PageWrapper
          isLoading={isLoading}
          isInitialLoad={isInitialLoad}
          isAuthPage={isAuthPage}
          assetsUrl={assetsUrl}
          isStudentApp={isStudentApp}
        >
          {element}
        </PageWrapper>
      </>
    );
  }
}

function useSession(): ISessionContext {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionContext Provider');
  }
  return context;
}

export { Authentication, useSession };
